import { useEffect, useRef } from 'react';

const DEFAULT_IDLE_EVENTS = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
  'pointerdown',
];

/**
 * After `timeoutMs` without user activity, invoke `onIdle` once.
 * Timer is cleared while `enabled` is false.
 * Pass `activityKey` to reset on programmatic signals (e.g. sent message count).
 * Pass `events: []` to ignore DOM activity and only use `activityKey`.
 */
export function useIdleRedirect({ enabled, timeoutMs, onIdle, activityKey, events = DEFAULT_IDLE_EVENTS }) {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !timeoutMs || timeoutMs <= 0) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return undefined;
    }

    const schedule = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        onIdleRef.current?.();
      }, timeoutMs);
    };

    const onActivity = () => {
      schedule();
    };

    schedule();
    for (const eventName of events) {
      window.addEventListener(eventName, onActivity, { passive: true });
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      for (const eventName of events) {
        window.removeEventListener(eventName, onActivity);
      }
    };
  }, [enabled, timeoutMs, events, activityKey]);
}
